import {
  InMemoryFlagConfiguration,
  MultiProvider,
  Provider,
  TypedInMemoryProvider,
} from '@openfeature/web-sdk';

import { getFeatureFlagOverridesFromQueryParameters } from 'state/launchdarkly/feature-flag-overrides';
import { RBIEnv, checkRbiEnv } from 'utils/environment';

type FlagOverrides = ReturnType<typeof getFeatureFlagOverridesFromQueryParameters>;

/**
 * An overridden flag becomes a one-variant flag that always resolves to the
 * forced value. A flag nobody overrode gets no entry at all — an entry would
 * answer for it, and the provider behind this one would never be asked.
 */
export const buildDatadogFlagOverrideConfiguration = (
  overrides: FlagOverrides
): InMemoryFlagConfiguration =>
  Object.entries(overrides).reduce(
    (configuration, [flagKey, value]) => ({
      ...configuration,
      [flagKey]: {
        variants: { forced: value },
        defaultVariant: 'forced',
        disabled: false,
      },
    }),
    {} as InMemoryFlagConfiguration
  );

/**
 * Layers the `rbiFeature.` query-parameter overrides in front of the given
 * provider.
 *
 * `MultiProvider` resolves in order and skips a provider that reports the
 * flag as not found, so an in-memory provider holding only the overridden
 * flags answers for those and falls through for everything else. This is how
 * a capability that lives inside the LaunchDarkly hook today survives onto a
 * provider that knows nothing about it.
 *
 * Overrides are read only outside production, matching the LaunchDarkly hook.
 * A developer affordance that shipped to guests would let anyone handed a URL
 * flip any flag for themselves.
 */
export const buildDatadogFlagsProvider = (provider: Provider): Provider => {
  if (checkRbiEnv(RBIEnv.PROD)) {
    return provider;
  }

  const overrides = buildDatadogFlagOverrideConfiguration(
    getFeatureFlagOverridesFromQueryParameters()
  );

  return new MultiProvider([{ provider: new TypedInMemoryProvider(overrides) }, { provider }]);
};
